import React, { useState, useCallback, useEffect } from 'react';
import { searchCustomers, getHeaders } from '../api';
import { isUnlocked, unlockAccount, subscribeAccountSession } from '../lib/accountSession';

const API = import.meta.env.PROD ? '' : 'https://crm-api.squish.co';

function debounce(fn, delay) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

// ─── Create Account Modal ─────────────────────────────────────────────────────

function CreateAccountModal({ onClose, onCreate }) {
  const [form, setForm] = useState({
    Name: '', Phone: '', Type: 'Customer', Industry: '',
    BillingStreet: '', BillingCity: '', BillingState: '',
    BillingPostalCode: '', BillingCountry: '', Website: '', Description: '',
  });
  // Sprint 4: primary contact captured alongside the account so a rep on a
  // call can go from zero to "account + contact ready to cart" in one click.
  // The users registry (Tier 1) auto-links any future Firebase sign-in with
  // this email to the Contact created here, via the email match path.
  const [contact, setContact] = useState({
    firstName: '', lastName: '', email: '', phone: '', title: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const [warning, setWarning] = useState('');

  function set(key, val) { setForm(f => ({ ...f, [key]: val })); }
  function setC(key, val) { setContact(c => ({ ...c, [key]: val })); }

  // Has any contact field been touched? Used to decide whether to send the
  // primaryContact block on the backend.
  const contactFilled = Object.values(contact).some(v => v.trim());

  async function handleSubmit() {
    if (!form.Name.trim()) { setError('Account name is required.'); return; }
    if (contactFilled && !contact.lastName.trim()) {
      setError('Contact last name is required when other contact fields are filled.');
      return;
    }
    setSaving(true);
    setError('');
    setWarning('');
    try {
      const body = { ...form };
      if (contactFilled) body.primaryContact = contact;
      const res  = await fetch(`${API}/api/accounts`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create account');
      // Partial-success path: account created but contact leg failed. Still
      // navigate into the account — rep can retry the contact from there.
      if (contactFilled && !data.contact && data.contactError) {
        setWarning(`Account created, but contact could not be added: ${data.contactError}. You can add it from the Account detail page.`);
        // Delay the close so the rep sees the warning, then navigate.
        setTimeout(() => { onCreate({ ...data.account, accountToken: data.accountToken }); onClose(); }, 2500);
        setSaving(false);
        return;
      }
      onCreate({ ...data.account, accountToken: data.accountToken });
      onClose();
    } catch (e) {
      setError(e.message);
    }
    setSaving(false);
  }

  const inputStyle = {
    width: '100%', background: 'var(--bg)', border: '1px solid var(--border)',
    borderRadius: 8, padding: '9px 12px', color: 'var(--text)',
    fontFamily: 'var(--font)', fontSize: 13, outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle = {
    display: 'block', fontSize: 11, color: 'var(--text3)', fontWeight: 600,
    marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'var(--mono)',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }} onClick={onClose}>
      <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 16, width: '90%', maxWidth: 560, maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 25px 60px rgba(0,0,0,0.5)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '24px 24px 0' }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>New Account</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>Creates in Salesforce</div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--text3)', fontSize: 18, cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ padding: 24 }}>
          {error && <div style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid #f87171', color: '#f87171', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>{error}</div>}
          {warning && <div style={{ background: 'rgba(251,191,36,0.12)', border: '1px solid #fbbf24', color: '#fbbf24', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>⚠ {warning}</div>}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Account Name *</label>
            <input style={inputStyle} value={form.Name} onChange={e => set('Name', e.target.value)} placeholder="e.g. Acme Corp" />
          </div>
          <div className="m-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Type</label>
              <select style={inputStyle} value={form.Type} onChange={e => set('Type', e.target.value)}>
                {['Customer', 'Partner', 'Prospect', 'Competitor', 'Other'].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Industry</label>
              <input style={inputStyle} value={form.Industry} onChange={e => set('Industry', e.target.value)} placeholder="e.g. Technology" />
            </div>
          </div>
          <div className="m-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div><label style={labelStyle}>Phone</label><input style={inputStyle} value={form.Phone} onChange={e => set('Phone', e.target.value)} placeholder="+1 555 000 0000" /></div>
            <div><label style={labelStyle}>Website</label><input style={inputStyle} value={form.Website} onChange={e => set('Website', e.target.value)} placeholder="https://example.com" /></div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Street</label>
            <input style={inputStyle} value={form.BillingStreet} onChange={e => set('BillingStreet', e.target.value)} placeholder="123 Main St" />
          </div>
          <div className="m-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div><label style={labelStyle}>City</label><input style={inputStyle} value={form.BillingCity} onChange={e => set('BillingCity', e.target.value)} placeholder="Phoenix" /></div>
            <div><label style={labelStyle}>State</label><input style={inputStyle} value={form.BillingState} onChange={e => set('BillingState', e.target.value)} placeholder="AZ" /></div>
          </div>
          <div className="m-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div><label style={labelStyle}>Postal Code</label><input style={inputStyle} value={form.BillingPostalCode} onChange={e => set('BillingPostalCode', e.target.value)} placeholder="85001" /></div>
            <div><label style={labelStyle}>Country</label><input style={inputStyle} value={form.BillingCountry} onChange={e => set('BillingCountry', e.target.value)} placeholder="US" /></div>
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>Description</label>
            <textarea style={{ ...inputStyle, height: 72, resize: 'vertical' }} value={form.Description} onChange={e => set('Description', e.target.value)} placeholder="Optional notes..." />
          </div>

          {/* ─── Primary Contact (optional — Sprint 4 assisted signup) ─── */}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 18, marginBottom: 20 }}>
            <div style={{ marginBottom: 4, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>Primary Contact</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>optional</div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 12, lineHeight: 1.5 }}>
              Add now to send them cart links, welcome emails, and link their future squish.co sign-in to this account automatically.
            </div>
            <div className="m-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={labelStyle}>First Name</label>
                <input style={inputStyle} value={contact.firstName} onChange={e => setC('firstName', e.target.value)} placeholder="Casey" />
              </div>
              <div>
                <label style={labelStyle}>Last Name {contactFilled && <span style={{ color: '#f87171' }}>*</span>}</label>
                <input style={inputStyle} value={contact.lastName} onChange={e => setC('lastName', e.target.value)} placeholder="Payne" />
              </div>
            </div>
            <div className="m-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={labelStyle}>Email</label>
                <input style={{ ...inputStyle, fontFamily: 'var(--mono)' }} value={contact.email} onChange={e => setC('email', e.target.value)} placeholder="casey@acme.com" />
              </div>
              <div>
                <label style={labelStyle}>Phone</label>
                <input style={{ ...inputStyle, fontFamily: 'var(--mono)' }} value={contact.phone} onChange={e => setC('phone', e.target.value)} placeholder="+1 555 000 0000" />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Title</label>
              <input style={inputStyle} value={contact.title} onChange={e => setC('title', e.target.value)} placeholder="Owner, CTO, …" />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button onClick={onClose} style={{ padding: '8px 18px', background: 'transparent', color: 'var(--text2)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>Cancel</button>
            <button data-write onClick={handleSubmit} disabled={saving} style={{ padding: '8px 18px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Creating…' : 'Create Account'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Search Page ──────────────────────────────────────────────────────────────

// Opening an account is App's job (onOpenAccount): it owns the OTP modal and
// the sidebar's open-account tabs, so an account opened here is still open
// after a trip to Tickets and back.
export default function Search({ autoCreate, onAutoCreateDone, onOpenAccount }) {
  const [query, setQuery]           = useState('');
  const [results, setResults]       = useState([]);
  const [loading, setLoading]       = useState(false);
  const [searched, setSearched]     = useState(false);
  // `searchError`: surfaces backend failures (Salesforce auth, CF Access,
  // proxy hiccup) instead of silently falling back to the "Start typing"
  // empty state. Without this, an API 500 looked exactly like "no input
  // yet" to the rep, leaving them staring at the initial prompt with no
  // clue that the search was actually failing. Caught 2026-05-26.
  const [searchError, setSearchError] = useState(null);
  const [creating, setCreating]     = useState(false);

  useEffect(() => {
    if (autoCreate) { setCreating(true); onAutoCreateDone(); }
  }, [autoCreate]);

  // Unlock state lives in sessionStorage, not React state, so the "✓ unlocked"
  // markers in the result list need a nudge when an unlock lands (or is cleared
  // from another surface). Cheap: a counter bump, no data copied into state.
  const [, bumpUnlocks] = useState(0);
  useEffect(() => subscribeAccountSession(() => bumpUnlocks(n => n + 1)), []);

  const doSearch = useCallback(
    debounce(async (q) => {
      if (!q.trim()) { setResults([]); setSearched(false); setSearchError(null); return; }
      setLoading(true);
      setSearchError(null);
      try {
        const data = await searchCustomers(q);
        setResults(data);
        setSearched(true);
      } catch (err) {
        // Capture the upstream error so the UI can show what actually
        // failed, AND mark searched=true so the "no matches found" / error
        // surface renders instead of falling back to the initial prompt.
        console.error('[search] failed:', err);
        setResults([]);
        setSearched(true);
        setSearchError(err.message || 'Search failed — try again or contact the engineering team.');
      } finally {
        setLoading(false);
      }
    }, 400),
    []
  );

  const handleChange = (e) => { setQuery(e.target.value); doSearch(e.target.value); };

  function handleCreated(account) {
    // A brand-new account has no customer inbox to consent from yet — the
    // creator IS the first party to it — so the server mints an unlock token as
    // part of the create response. This used to store the literal string 'new'
    // as the token, which fails jwt.verify the moment the boundary is enforced:
    // the one account a staffer definitely should be able to open would have
    // been the one that 401'd. If the server didn't return a token, App falls
    // back to the OTP path rather than entering with nothing.
    if (account.accountToken) unlockAccount(account.Id, account.accountToken);
    onOpenAccount(account.Id, { name: account.Name, number: account.AccountNumber });
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Customer Lookup</h1>
        <p className="page-subtitle">Search by name, email, account number, or phone</p>
      </div>

      <div style={{ position: 'relative', marginBottom: 28, maxWidth: 560 }}>
        <span style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', fontSize: 16 }}>⌕</span>
        <input
          autoFocus
          value={query}
          onChange={handleChange}
          placeholder="Search by name, email, account #, or phone number..."
          style={{ width: '100%', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px 14px 44px', color: 'var(--text)', fontFamily: 'var(--font)', fontSize: 15, outline: 'none', transition: 'border-color 0.2s', boxSizing: 'border-box' }}
          onFocus={e => e.target.style.borderColor = 'var(--accent)'}
          onBlur={e => e.target.style.borderColor = 'var(--border)'}
        />
        {loading && (
          <div style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', display: 'flex', gap: 3 }}>
            <span className="loading-dot" /><span className="loading-dot" /><span className="loading-dot" />
          </div>
        )}
      </div>

      {searchError && (
        <div style={{
          background: 'rgba(248,113,113,0.1)',
          border: '1px solid #f87171',
          color: '#fca5a5',
          borderRadius: 10,
          padding: '14px 16px',
          marginBottom: 16,
          fontFamily: 'var(--mono)',
          fontSize: 13,
          lineHeight: 1.5,
        }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>⚠ Search failed</div>
          <div style={{ opacity: 0.85 }}>{searchError}</div>
          <div style={{ opacity: 0.6, fontSize: 11, marginTop: 6 }}>
            Check the browser console + Network tab for details. If this persists, the backend (Salesforce auth, CF Access proxy, or service token) is likely the cause.
          </div>
        </div>
      )}

      {searched && !searchError && results.length === 0 && (
        <div style={{ color: 'var(--text3)', fontFamily: 'var(--mono)', fontSize: 13, padding: '24px 0' }}>No accounts found for "{query}"</div>
      )}

      {results.length > 0 && (
        <div className="table-card">
          <div className="table-header">
            <span className="table-title">Results</span>
            <span className="table-count">{results.length}</span>
          </div>
          <table className="m-cards">
            <thead><tr><th>Account #</th><th>Name</th><th>Type</th><th>Industry</th><th>Phone</th><th>Location</th></tr></thead>
            <tbody>
              {results.map(r => (
                <tr key={r.Id} onClick={() => onOpenAccount(r.Id, { name: r.Name, number: r.AccountNumber })} style={{ cursor: 'pointer' }}>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--accent)', fontWeight: 700 }}>{r.AccountNumber || '—'}</td>
                  <td className="m-card-title" style={{ color: 'var(--text)', fontWeight: 600 }}>
                    {r.Name}
                    {isUnlocked(r.Id) && <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--accent3)', fontFamily: 'var(--mono)' }}>✓ unlocked</span>}
                  </td>
                  <td>{r.Type || '—'}</td>
                  <td>{r.Industry || '—'}</td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{r.Phone || '—'}</td>
                  <td style={{ color: 'var(--text3)', fontSize: 12 }}>{[r.BillingCity, r.BillingState].filter(Boolean).join(', ') || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!searched && !loading && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 280, color: 'var(--text3)', gap: 12 }}>
          <div style={{ fontSize: 48 }}>⌕</div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>Start typing to search customers</div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text3)', opacity: 0.6 }}>Search by name, email, account number, or phone</div>
        </div>
      )}

      {creating && <CreateAccountModal onClose={() => setCreating(false)} onCreate={handleCreated} />}
    </div>
  );
}